import { useCallback, useEffect, useRef } from 'react';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { syncFromAnotherSource } from '@atlaskit/prosemirror-collab';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { ACTION } from '@atlaskit/editor-common/analytics';

import type { StepsDatabase } from './stepsDatabase';
import type { EditorOfflineStoragePlugin } from './editorOfflineStoragePluginType';
import { resetProsemirrorHistoryPlugin } from './history';
import { fireOfflineStorageAnalyticsEvent } from './analytics';

export type BroadcastType = 'resync' | 'restore';

type MessageEventPayload = {
	type: BroadcastType;
};

/**
 * Props for the useBroadcastChannel hook
 * @param api - The public plugin api
 * @param editorView - The editor view
 * @param stepsDatabase - The steps database
 */
type UseBroadcastChannelProps = {
	api: PublicPluginAPI<EditorOfflineStoragePlugin> | undefined;
	editorView: EditorView;
	stepsDatabase: StepsDatabase;
};

/**
 * Listens for update events broadcasted over the broadcast channel.
 * When the update event is recieved, the listener grabs the doc, version and steps from the database and calls the syncFromAnotherSource
 * function which creates a transaction to update the editor state. The transaction is then dispatched to the editor view.
 * The history plugin is also reset in this process since there is currently no way to update the history plugin state to align with the
 * new state.
 *
 * @returns A function to broadcast when an update event has occured
 */
export const useBroadcastChannel = ({
	api,
	editorView,
	stepsDatabase,
}: UseBroadcastChannelProps) => {
	const isBroadcastChannelSupported = window && 'BroadcastChannel' in window;
	const channelRef = useRef<BroadcastChannel | undefined>();
	const firstChannelUpdateRef = useRef(false);

	const broadcast = useCallback((messageType: MessageEventPayload['type'] = 'resync') => {
		if (channelRef.current) {
			const message: MessageEventPayload = {
				type: messageType,
			};
			channelRef.current.postMessage(message);
		}
	}, []);

	const listener = useCallback(
		async function (this: BroadcastChannel, event: MessageEvent<MessageEventPayload>) {
			const { type } = event.data;
			if (type !== 'resync' && type !== 'restore') {
				return;
			}
			const { mode } = api?.connectivity?.sharedState.currentState() ?? {};

			// RESYNC: Only proceed when offline. This happens while syncing tabs when offline.
			// RESTORE: Only proceed when online. We restore on initialisation, when we have a connection.
			if (type === 'resync') {
				if (mode !== 'offline') {
					return;
				}
			} else if (type === 'restore') {
				if (mode !== 'online') {
					return;
				}
			}

			const {
				steps,
				docJSON,
				version: cachedVersion,
			} = await stepsDatabase.getData(editorView.state.schema);

			if (!docJSON || cachedVersion === null) {
				return;
			}

			// RESYNC: only proceed when there are steps to sync.
			// RESTORE: only proceed when there are no steps to sync. The docJSON should have the cleared steps already applied.
			if (type === 'resync') {
				if (!steps) {
					return;
				}
			} else if (type === 'restore') {
				if (steps && steps.length > 0) {
					return;
				}
			}

			if (!firstChannelUpdateRef.current) {
				fireOfflineStorageAnalyticsEvent({
					api,
					action: ACTION.OFFLINE_STORAGE_BROADCAST_CHANNEL_FIRST_UPDATE,
				});
				firstChannelUpdateRef.current = true;
			}

			const historyState = api?.history?.sharedState?.currentState?.();
			const hasHistory =
				(historyState?.done?.eventCount ?? 0) > 0 || (historyState?.undone?.eventCount ?? 0) > 0;
			if (hasHistory) {
				resetProsemirrorHistoryPlugin(editorView);
			}

			const tr: Transaction = syncFromAnotherSource(
				editorView.state,
				cachedVersion,
				docJSON,
				steps,
			);

			if (hasHistory && api?.history?.commands.updatePluginState) {
				api.history.commands.updatePluginState({ tr });
			}
			editorView.dispatch(tr);
		},
		[api, editorView, stepsDatabase],
	);

	useEffect(() => {
		if (!isBroadcastChannelSupported || !stepsDatabase.dbName) {
			return;
		}

		channelRef.current = new BroadcastChannel(stepsDatabase.dbName);
		channelRef.current.addEventListener('message', listener);
		return () => {
			if (channelRef.current) {
				channelRef.current.removeEventListener('message', listener);
				channelRef.current.close();
			}
		};
	}, [isBroadcastChannelSupported, stepsDatabase.dbName, listener]);

	return broadcast;
};
